import React, { useEffect } from 'react'
import { connect as connectRedux } from 'react-redux'
import ImmutablePropTypes from 'react-immutable-proptypes'
import PropTypes from 'prop-types'
import { getBoundActions } from '../../actions/index'
import { H1, HEADING_TYPES } from '../Heading/index'
import Button, { TYPES } from '../Button/index'
import { URL_CHECKOUT } from '../../services/url/constants'
import { SPOTLIGHT_IMPRESSION_GA4_EVENT, RESTART_SUBSCRIPTION_CLICKED_GA4_EVENT } from '../../services/event-tracking/index'
import { historyRedirect } from '../../services/navigation/index'

/**
 * Displays a winback banner for users who have completed checkout but are not entitled
 * to access content. This banner encourages users to restart their subscription.
 *
 * @param {Object} props - Component props
 * @param {Object} props.auth - The auth object
 * @param {Object} props.history - The history object
 * @param {Function} props.sendOptimizelyFeatureEvent - Function to send the optimizely events
 * @param {Function} props.setEventDefault - Function to set the default GA4 event
 * @param {Object} props.staticText - The static text translations
 * @param {Object} props.userLanguage - The user language
 * @returns {JSX.Element} - Winback banner component
 */
function SpotlightWinbackBannerV2 (props) {
  const {
    auth,
    history,
    sendOptimizelyFeatureEvent,
    setEventDefault,
    staticText,
    userLanguage,
  } = props

  useEffect(() => {
    setEventDefault(SPOTLIGHT_IMPRESSION_GA4_EVENT.mergeDeep({
      row_number: 0,
      row_placement: 0,
      row_type: 'member-home-spotlight-a-v1',
      row_name: 'winback banner',
      has_trailer: false,
      spotlight_type: 'winback banner',
    }))
  }, [])

  const onCtaClick = () => {
    // GA4 event
    setEventDefault(RESTART_SUBSCRIPTION_CLICKED_GA4_EVENT.mergeDeep({
      ui_element: 'Spotlight',
    }))

    // Optimizely event
    sendOptimizelyFeatureEvent({ eventName: 'restart_subscription_clicked' })

    // Redirect to account creation page
    historyRedirect({
      history, url: URL_CHECKOUT, auth, language: userLanguage,
    })
  }

  return (
    <div className="spotlight-winback-banner-v2">
      <div className="spotlight-winback-banner-v2__overlay" />
      <div className="spotlight-winback-banner-v2__content">
        <H1
          as={HEADING_TYPES.H4}
          inverted
          className="spotlight-winback-banner-v2__title"
        >
          {staticText.get('welcomeBack')}
        </H1>
        <p className="spotlight-winback-banner-v2__teaser">
          {staticText.get('weGladToSeeYou')}
        </p>
        <Button
          type={TYPES.PRIMARY}
          scrollToTop
          onClick={onCtaClick}
          className="spotlight-winback-banner-v2__cta"
        >
          {staticText.get('restartSubscription')}
      </Button>
      </div>
    </div>
  )
}

SpotlightWinbackBannerV2.propTypes = {
  staticText: ImmutablePropTypes.map.isRequired,
  history: PropTypes.object.isRequired,
}

export default connectRedux(
  (state) => {
    return {
      staticText: state.staticText.getIn(['data', 'spotlightWinbackBanner', 'data']),
      upstreamContext: state.upstreamContext,
      auth: state.auth,
      userLanguage: state.user.getIn(['data', 'language']),
    }
  },
  (dispatch) => {
    const actions = getBoundActions(dispatch)
    return {
      setEventDefault: actions.eventTracking.setEventDefault,
      sendOptimizelyFeatureEvent: actions.optimizelyFeature.sendOptimizelyFeatureEvent,
    }
  },
)(SpotlightWinbackBannerV2)
